import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Box, CardActionArea, DialogContent, Stack, Toolbar } from "@mui/material";
import styles from "./styles.module.scss";
import { strings } from "../../localization/LocalizedStrings";
import { ToolbarButton } from "../toolbar/ToolbarButton";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { useImportFromSpreadsheet } from "../../hooks/xlsx/useImportFromSpreadsheet";
import { CollectionsDialog } from "../collections/CollectionsDialog";
import { ICollection } from "../../hooks/collections";
import { StringFormat } from "../../models/StringFormat";
import { AlertDialog } from "../AlertDialog";
import { StatusCodesHelper } from "../../models/StatusCodesHelper";
import { ContainedButton } from "../ContainedButton";
import { RoutesBuilder } from "../../models/RoutesBuilder";
import { Firebase } from "../../services/Firebase";
import { ProductPurchasesDialog } from "../products/ProductPurchasesDialog";
import { ProductType } from "../../hooks/products/interfaces";
import { PiperDialog } from "../PiperDialog";
import { IconType } from "../../icons";
import { useUser } from "../../hooks/session";
import { useGetSpreadsheetImportStatus } from "../../hooks/xlsx/useGetSpreadsheetImportStatus";
import { IOngoingTask } from "../../models/OngoingTask";
import { useConfirmSpreadsheetImport } from "../../hooks/xlsx/useConfirmSpreadsheetImport";
import { useCancelSpreadsheetImport } from "../../hooks/xlsx/useCancelSpreadsheetImport";
import { ImportProgressView } from "../ImportProgressView";

export const ExcelImportDialog: FunctionComponent<IExcelImportDialogProps> = ({
  isOpen,
  setIsOpen,
  isLayout
}) => {
  const [state, setState] = useState<State>("starting");
  const [file, setFile] = useState<File & { preview: string }>();
  const { importFromSpreadsheet } = useImportFromSpreadsheet();
  const [currentUser] = useUser.useState();
  const [isCollectionsDialogOpen, setIsCollectionsDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<ICollection>();
  const [isConfirmImportAlertOpen, setIsConfirmImportAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isPurchaseAlertOpen, setIsPurchaseAlertOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const getSpreadsheetImportStatus = useGetSpreadsheetImportStatus();
  const confirmSpreadsheetImport = useConfirmSpreadsheetImport();
  const cancelSpreadsheetImport = useCancelSpreadsheetImport();
  const [task, setTask] = useState<IOngoingTask>();
  const [intervalId, setIntervalId] = useState<number>();
  const startUp = async () => {
    setIsLoading(true);
    const { body, status } = await getSpreadsheetImportStatus();
    Firebase.shared.logFirebaseEvent("import_spreadsheet_init");
    if (StatusCodesHelper.isSuccessful(status)) {
      setTask(body);
      if (body.status != "confirmed") {
        setState("importing");
      } else {
        setState("uploading");
      }
    } else {
      setState("uploading");
    }
    setIsLoading(false);
  };
  const getImportStatus = async () => {
    const { body, status } = await getSpreadsheetImportStatus();
    if (StatusCodesHelper.isSuccessful(status)) {
      setTask(body);
    }
  };
  useEffect(() => {
    if (!["running", "created"].includes(task?.status)) {
      clearTimer();
      return;
    }
    if (intervalId == undefined) {
      const id = setInterval(async () => {
        await getImportStatus();
      }, 2000);
      setIntervalId(Number(id));
    }
  }, [task]);
  const clearTimer = () => {
    clearInterval(intervalId);
    setIntervalId(undefined);
  };
  useEffect(() => {
    if (!isOpen) return;
    setState("starting");
    setFile(undefined);
    startUp().then();
  }, [isOpen]);
  const handleClose = async () => {
    if (isLoading) return;
    clearTimer();
    if (state == "importing" && task && !["running", "created"].includes(task.status)) {
      setIsLoading(true);
      await confirmSpreadsheetImport();
      setIsLoading(false);
    }
    setIsOpen(false);
  };
  const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    padding: "80px 20px 80px 20px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out"
  };
  const focusedStyle = {
    borderColor: "#2196f3"
  };
  const acceptStyle = {
    borderColor: "#00e676"
  };
  const rejectStyle = {
    borderColor: "#ff1744"
  };
  const importSubtitle = () => {
    switch (task?.status) {
      case "created":
        return strings.discogs_import_created;
      case "running":
        return strings.discogs_import_subtitle;
      case "finished":
        return strings.discogs_import_finished;
      case "cancelled":
        return strings.discogs_import_cancelled;
      case "errored":
        return strings.discogs_import_errored;
    }
  };
  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [] },
    maxFiles: 1,
    onDrop: (acceptedFiles, filesRejection) => {
      if (filesRejection.length > 0) {
        toast.error(strings.upload_multiple_files_error);
        return;
      }
      const file = Object.assign(acceptedFiles[0], {
        preview: URL.createObjectURL(acceptedFiles[0])
      });
      setFile(file);
    }
  });
  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isFocused, isDragAccept, isDragReject]
  );
  const uploadFile = async () => {
    setIsCollectionsDialogOpen(false);
    setIsLoading(true);
    if (!file) return;
    const { status, body } = await importFromSpreadsheet(
      file,
      selectedCollection ? [selectedCollection] : [],
      currentUser.uuid
    );
    if (StatusCodesHelper.isSuccessful(status)) {
      setTask(body);
      setState("importing");
    } else if (StatusCodesHelper.isLimitReached(status)) {
      setAlertMessage(body.message);
      setIsPurchaseAlertOpen(true);
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <PiperDialog
      fullWidth
      maxWidth={"sm"}
      open={isOpen}
      onClose={handleClose}
      PaperProps={{ style: { minHeight: "320px" } }}
    >
      <Toolbar>
        <h3>{strings.excel_import_title}</h3>
        <Box flexGrow={1} />
        <ToolbarButton
          href={`${window.location.protocol}//${
            window.location.host
          }${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.album.importFromExcel()}`}
          target={"_blank"}
          tooltip={strings.help}
          iconType={IconType.Help}
        />
        <ToolbarButton
          onClick={handleClose}
          tooltip={strings.close}
          iconType={IconType.Close}
          loading={isLoading}
        />
      </Toolbar>
      <DialogContent className={styles.content}>
        {state === "uploading" ? (
          <Stack>
            <p className={styles.message}>{strings.excel_import_message}</p>
            <ContainedButton
              className={styles.templateButton}
              href={strings.excel_import_template_url}
            >
              {strings.excel_import_download_template}
            </ContainedButton>
            <CardActionArea {...getRootProps({ style })}>
              <input {...getInputProps()} />
              <p className={styles.dropzonePlaceholder}>
                {file ? file.name : strings.dropzone_placeholder}
              </p>
            </CardActionArea>
            <ContainedButton
              className={styles.confirmButton}
              disabled={file === undefined || isLoading}
              onClick={() => setIsCollectionsDialogOpen(true)}
            >
              {strings.alert_confirm}
            </ContainedButton>
          </Stack>
        ) : (
          state === "importing" && (
            <ImportProgressView
              task={task}
              subtitle={importSubtitle()}
              runningDescription={strings.excel_import_description}
              disclaimer={
                ["created", "running"].includes(task.status)
                  ? strings.discogs_import_disclaimer
                  : strings.discogs_import_disclaimer_finished
              }
              isLoading={isLoading}
              onCancel={async () => {
                setIsLoading(true);
                const { body, status } = await cancelSpreadsheetImport();
                if (StatusCodesHelper.isSuccessful(status)) {
                  setTask(body);
                } else {
                  setAlertMessage(body.message);
                  setIsAlertOpen(true);
                }
                setIsLoading(false);
              }}
            />
          )
        )}
      </DialogContent>
      <CollectionsDialog
        open={isCollectionsDialogOpen}
        setOpen={setIsCollectionsDialogOpen}
        isLayout={isLayout}
        canModifyCollections={false}
        displayStandardCollections={true}
        onCollectionSelected={collection => {
          setSelectedCollection(collection);
          setIsConfirmImportAlertOpen(true);
        }}
        selectedCollections={[]}
        forceLoading={false}
        canNegate={false}
      />
      <AlertDialog
        message={StringFormat(
          strings.excel_import_confirmation,
          file?.name ?? "",
          selectedCollection?.name ?? ""
        )}
        open={isConfirmImportAlertOpen}
        setOpen={setIsConfirmImportAlertOpen}
        isConfirm
        onConfirm={async () => uploadFile()}
      />
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
      <AlertDialog
        message={alertMessage}
        open={isPurchaseAlertOpen}
        setOpen={setIsPurchaseAlertOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setIsPurchaseDialogOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.ALBUMS}
        open={isPurchaseDialogOpen}
        setOpen={setIsPurchaseDialogOpen}
      />
    </PiperDialog>
  );
};

type State = "starting" | "uploading" | "importing";

export interface IExcelImportDialogProps {
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
  isLayout: boolean;
}
